import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  ApiError,
  LoginRequest,
  RegisterRequest,
  LoginData,
  User,
} from '../../types';

import authService from '../../services/api/authService';
import { clearPhotoUrlCache } from '../../services/api/imageService';
export const login = createAsyncThunk<
  LoginData,
  LoginRequest,
  { rejectValue: ApiError }
>(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      clearPhotoUrlCache();
      const response = await authService.login(email, password);

      const loginData: LoginData = {
        token: response.token,
        userId: response.userId,
        email: response.email ?? undefined, 
      };

      return loginData;
    } catch (error: any) {
      const apiError: ApiError = {
        message:
          error?.message ||
          'Login failed. Please check your email or password.',
        code: error?.code || 'LOGIN_FAILED',
      };

      return rejectWithValue(apiError);
    }
  }
);

export const register = createAsyncThunk<
  User,
  RegisterRequest,
  { rejectValue: ApiError }
>(
  'auth/register',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authService.register(email, password);

      const user: User = {
        id: response.userId,
        email: response.email ?? undefined, 
      };

      return user;
    } catch (error: any) {
      const apiError: ApiError = {
        message:
          error?.message ||
          'Registration failed. Please try again later.',
        code: error?.code || 'REGISTRATION_FAILED',
      };

      return rejectWithValue(apiError);
    }
  }
);


export const logout = createAsyncThunk<void, void>(
  'auth/logout',
  async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearPhotoUrlCache();
    }
  }
);


export const refreshToken = createAsyncThunk<
  string | null,
  void,
  { rejectValue: ApiError }
>(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      return await authService.refreshToken();
    } catch (error: any) {
      return rejectWithValue({
        message: 'Failed to refresh authentication token',
        code: 'TOKEN_REFRESH_FAILED',
      });
    }
  }
);
